import React, { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontal, Loader2, RotateCcw, Check, ShieldCheck, Lock } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ScoringConfig } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, PageHeader, Toggle } from '../dashboards/ui';

// The trust-contract rules the platform enforces everywhere (R1/R2/R3 · MIN_GROUP_SIZE=5).
// Shown here as locked/on — they're real product-wide behavior, not per-workspace toggles.
const PRIVACY_RULES = [
  { label: 'Show ranges, never point numbers', note: 'Self-report runs 30–40% high — a band is the honest read.' },
  { label: 'Discount self-report until telemetry matures', note: 'Confidence-weighted leading indicator.' },
  { label: 'Suppress groups under the privacy threshold', note: 'Minimum group size: 5 people.' },
  { label: 'Individual numbers stay private (aggregates only)', note: 'Same math, no ranking optics.' },
];

// Feature 2, P0 — the maturity formula lives in admin settings, not in code.
// Weights are relative (the server normalizes them to sum to 1); thresholds name
// the stage bands and must strictly increase. Defaults reproduce the prior
// hardcoded behavior, so leaving this untouched changes nothing.
const DIM_LABELS: Record<string, string> = {
  literacy: 'AI literacy',
  tool_adoption: 'Tool adoption',
  usage_depth: 'Usage depth',
  workflow: 'Workflow sophistication',
  output_impact: 'Output impact',
};
const THRESHOLD_ORDER = ['early', 'developing', 'mature', 'advanced'];
const pct = (v: number) => Math.round(v * 100);

const ScoringSettingsPage: React.FC = () => {
  const [cfg, setCfg] = useState<ScoringConfig | null>(null);
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const hydrate = (c: ScoringConfig) => {
    setCfg(c);
    setWeights({ ...c.weights });
    setThresholds({ ...c.thresholds });
  };
  const load = useCallback(async () => {
    setLoading(true);
    try { hydrate((await aaiApi.getScoringConfig()).data); setError(null); }
    catch { setError('Could not load scoring settings.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const weightTotal = Object.values(weights).reduce((a, b) => a + (b > 0 ? b : 0), 0) || 1;

  const save = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      hydrate((await aaiApi.updateScoringConfig({ weights, thresholds })).data);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Could not save — check the thresholds increase in order.');
    } finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading scoring settings…" />;
  if (error && !cfg) return <ErrorNote message={error} />;
  if (!cfg) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        kicker="Administration"
        title="Maturity scoring"
        subtitle="Define how the dashboard turns readiness into a 0–100% maturity score. Weights set how much each dimension counts; thresholds name the stage bands. Changes apply instantly to every dashboard — no re-onboarding."
      />

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <MonoLabel className="flex items-center gap-1.5"><SlidersHorizontal size={13} /> Dimension weights</MonoLabel>
          <span className="text-[11px] text-gray-400">relative · normalized to 100%</span>
        </div>
        <div className="space-y-3.5">
          {cfg.dimensions.map((d) => {
            const w = weights[d] ?? 0;
            const share = Math.round((Math.max(w, 0) / weightTotal) * 100);
            return (
              <div key={d}>
                <div className="flex items-center justify-between text-[12.5px] mb-1">
                  <span className="font-medium text-gray-800">{DIM_LABELS[d] || d}</span>
                  <span className="font-sans text-gray-500">{share}%</span>
                </div>
                <input type="range" min={0} max={100} value={pct(w)}
                  onChange={(e) => setWeights((prev) => ({ ...prev, [d]: Number(e.target.value) / 100 }))}
                  className="w-full accent-brand-500" />
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="mb-4">
        <MonoLabel className="mb-3">Stage thresholds</MonoLabel>
        <p className="text-[12px] text-gray-500 mb-3">A person's score lands in the highest band whose threshold it clears (below the first = <span className="font-semibold">nascent</span>). Must increase in order.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {THRESHOLD_ORDER.map((s) => (
            <div key={s}>
              <label className="block text-[11.5px] font-semibold text-gray-700 capitalize mb-1">{s}</label>
              <div className="flex items-center gap-1">
                <input type="number" min={1} max={99} value={pct(thresholds[s] ?? 0)}
                  onChange={(e) => setThresholds((prev) => ({ ...prev, [s]: Number(e.target.value) / 100 }))}
                  className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <span className="text-[12px] text-gray-400">%</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <MonoLabel className="flex items-center gap-1.5"><ShieldCheck size={13} /> Privacy & honesty rules</MonoLabel>
          <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-gray-400"><Lock size={11} /> Platform-enforced</span>
        </div>
        <p className="text-[12px] text-gray-500 mb-3">The trust contract behind every number — enforced product-wide, the same math without ranking optics.</p>
        <div className="divide-y divide-gray-100">
          {PRIVACY_RULES.map((r) => (
            <div key={r.label} className="flex items-start justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-gray-800">{r.label}</div>
                <div className="text-[11.5px] text-gray-500">{r.note}</div>
              </div>
              <Toggle on disabled />
            </div>
          ))}
        </div>
      </Card>

      {error && <ErrorNote message={error} />}

      <div className="flex items-center gap-3 mt-4">
        <button disabled={busy} onClick={save}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 whitespace-nowrap">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save formula
        </button>
        <button disabled={busy} onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-50">
          <RotateCcw size={13} /> Reset
        </button>
        {saved && <span className="text-[12.5px] text-emerald-600 font-semibold">Saved.</span>}
        {!cfg.customized && <span className="text-[11.5px] text-gray-400 ml-auto">Currently using defaults.</span>}
      </div>
    </div>
  );
};

export default ScoringSettingsPage;
